import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "src/config/axios";
import { Badge, Button, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { faPencil as PencilIcon } from "@fortawesome/free-solid-svg-icons";
import { dateFormatter, dateTimeFormatter } from "src/global";
import useFilter from "src/global/useFilter";
import {
  showSuccessToast,
  showValidationErrorToast,
} from "src/components/Toasts";
import LocalTaxDailyReportDetailsDialog from "./LocalTaxDailyReportDetailsDialog";
import EditOccupiedUnitsDialog from "./EditOccupiedUnitsDialog";
import { monthOptions, statusVariants, yearOptions } from "./helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

function currentMonthPeriod() {
  const currentMonth = moment();

  return {
    month: currentMonth.month() + 1,
    year: currentMonth.year(),
  };
}

const DailyReportsPage = () => {
  const { t } = useTranslation("dashboard");
  const { pathname } = useLocation();
  const { filter, setFilter } = useFilter(pathname, currentMonthPeriod());
  const pollingRequestActive = useRef(false);
  const initialFetchDone = useRef(false);
  const [reports, setReports] = useState([]);
  const [loadedMonth, setLoadedMonth] = useState(filter.month);
  const [loadedYear, setLoadedYear] = useState(filter.year);
  const [loading, setLoading] = useState(false);
  const [sendingMonth, setSendingMonth] = useState(false);
  const [monthClosed, setMonthClosed] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [selectedOccupiedUnitsReport, setSelectedOccupiedUnitsReport] =
    useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const reportRows = useMemo(() => {
    const startOfMonth = moment({
      year: loadedYear,
      month: loadedMonth - 1,
      date: 1,
    });
    const reportsByDate = reports.reduce((result, report) => {
      result[moment(report.report_date).format("YYYY-MM-DD")] = report;
      return result;
    }, {});

    return Array.from({ length: startOfMonth.daysInMonth() }, (_, index) => {
      const reportDate = startOfMonth
        .clone()
        .add(index, "days")
        .format("YYYY-MM-DD");

      return {
        report_date: reportDate,
        status: "open",
        ...reportsByDate[reportDate],
      };
    });
  }, [reports, loadedMonth, loadedYear]);

  const hasPendingRows = useMemo(
    () => reportRows.some((report) => report.status === "pending"),
    [reportRows]
  );

  function fetchReports(month = filter.month, year = filter.year) {
    setLoading(true);
    setErrorMessage(null);

    axios
      .get("/api/v1/local_tax_daily_reports.json", { params: { month, year } })
      .then(({ data }) => {
        setLoadedMonth(month);
        setLoadedYear(year);
        setReports(data.entries || []);
        setMonthClosed(Boolean(data.month_closed));
      })
      .catch((error) => {
        console.error(error);
        showValidationErrorToast(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const refreshLoadedReports = useCallback(() => {
    if (pollingRequestActive.current || loading) return;

    pollingRequestActive.current = true;

    axios
      .get("/api/v1/local_tax_daily_reports.json", {
        params: { month: loadedMonth, year: loadedYear },
      })
      .then(({ data }) => {
        setReports(data.entries || []);
        setMonthClosed(Boolean(data.month_closed));
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        pollingRequestActive.current = false;
      });
  }, [loadedMonth, loadedYear, loading]);

  useEffect(() => {
    if (initialFetchDone.current) return;

    initialFetchDone.current = true;
    fetchReports(filter.month, filter.year);
  });

  useEffect(() => {
    if (!hasPendingRows) return undefined;

    const intervalId = window.setInterval(() => {
      refreshLoadedReports();
    }, 2000);

    return () => window.clearInterval(intervalId);
  }, [hasPendingRows, loadedMonth, loadedYear, refreshLoadedReports]);

  function sendDayReport(reportDate) {
    setErrorMessage(null);

    axios
      .post("/api/v1/local_tax_daily_reports/send_report.json", {
        report_date: reportDate,
      })
      .then(({ data }) => {
        replaceReport(data);
      })
      .catch((error) => {
        handleSendError(error);
      });
  }

  function sendMonth() {
    setSendingMonth(true);
    setErrorMessage(null);

    axios
      .post("/api/v1/local_tax_daily_reports/send_month_report.json", {
        month: loadedMonth,
        year: loadedYear,
      })
      .then(({ data }) => {
        setReports(data.entries || []);
        showSuccessToast(t("local_tax_daily_reports.messages.month_started"));
      })
      .catch((error) => {
        console.error(error);
        showValidationErrorToast(error);
      })
      .finally(() => {
        setSendingMonth(false);
      });
  }

  function handleSendError(error, setVisibleError = true) {
    console.error(error);

    if (error.response?.data?.error_message) {
      replaceReport(error.response.data);

      if (setVisibleError) {
        setErrorMessage(error.response.data.error_message);
      }
    } else if (setVisibleError) {
      setErrorMessage(t("local_tax_daily_reports.messages.send_failed"));
    }
  }

  function replaceReport(updatedReport) {
    if (!updatedReport?.report_date) return;

    setReports((currentReports) => {
      const remainingReports = currentReports.filter(
        (report) =>
          moment(report.report_date).format("YYYY-MM-DD") !==
          moment(updatedReport.report_date).format("YYYY-MM-DD")
      );

      return [...remainingReports, updatedReport];
    });
  }

  function handleOccupiedUnitsSaved(updatedReport) {
    replaceReport(updatedReport);
    setSelectedOccupiedUnitsReport(null);
    showSuccessToast("Belegte Einheiten wurden gespeichert.");
  }

  function statusLabel(status) {
    return t("local_tax_daily_reports.statuses." + status, {
      defaultValue: status,
    });
  }

  function statusVariant(status) {
    return statusVariants[status] || "secondary";
  }

  function actionLabel(status) {
    return t("local_tax_daily_reports.actions." + status, {
      defaultValue: t("local_tax_daily_reports.actions.open"),
    });
  }

  function tableValue(value) {
    return value === null || value === undefined ? "-" : value;
  }

  return (
    <div className="pt-3">
      {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}

      <Row className="align-items-end">
        <Col md={3} xs={12}>
          <Form.Group>
            <Form.Label>{t("local_tax_reports.period.month")}</Form.Label>
            <Form.Control
              as="select"
              value={filter.month}
              onChange={(event) =>
                setFilter({
                  ...filter,
                  month: Number(event.target.value),
                })
              }
              disabled={loading || sendingMonth}
            >
              {monthOptions.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={3} xs={12}>
          <Form.Group>
            <Form.Label>{t("local_tax_reports.period.year")}</Form.Label>
            <Form.Control
              as="select"
              value={filter.year}
              onChange={(event) =>
                setFilter({
                  ...filter,
                  year: Number(event.target.value),
                })
              }
              disabled={loading || sendingMonth}
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={6} xs={12}>
          <div className="mb-3">
            <Button
              variant="primary"
              onClick={() => fetchReports()}
              disabled={loading || sendingMonth}
              className="mr-2"
            >
              {loading
                ? t("local_tax_reports.actions.loading")
                : t("local_tax_reports.actions.refresh")}
            </Button>
            <Button
              variant="secondary"
              onClick={sendMonth}
              disabled={loading || sendingMonth || monthClosed}
            >
              {sendingMonth && (
                <Spinner animation="border" className="mr-2" size="sm" />
              )}
              {t("local_tax_daily_reports.actions.send_month")}
            </Button>
          </div>
        </Col>
      </Row>

      <p className="text-muted">{t("local_tax_daily_reports.description")}</p>

      {reports.length === 0 && (
        <div className="alert alert-info">
          {t("local_tax_daily_reports.empty")}
        </div>
      )}

      {loading ? (
        <div className="text-center p-4">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <div className="ui-table-container mb-3">
          <Table responsive>
            <thead>
              <tr>
                <th>{t("local_tax_daily_reports.table.date")}</th>
                <th>{t("local_tax_daily_reports.table.status")}</th>
                <th>{t("local_tax_daily_reports.table.arrivals")}</th>
                <th>{t("local_tax_daily_reports.table.departures")}</th>
                <th>{t("local_tax_daily_reports.table.occupied_units")}</th>
                <th>{t("local_tax_daily_reports.table.reported_at")}</th>
                <th>{t("local_tax_daily_reports.table.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {reportRows.map((report) => (
                <tr key={report.report_date}>
                  <td>{dateFormatter(report.report_date)}</td>
                  <td>
                    <Badge
                      pill
                      variant={statusVariant(report.status)}
                      text="light"
                    >
                      {statusLabel(report.status)}
                    </Badge>
                  </td>
                  <td>{tableValue(report.arrivals_count)}</td>
                  <td>{tableValue(report.departures_count)}</td>
                  <td>
                    <span className="mr-2">
                      {tableValue(report.occupied_units)}
                    </span>
                    {report.id && (
                      <FontAwesomeIcon
                        icon={PencilIcon}
                        onClick={() => setSelectedOccupiedUnitsReport(report)}
                        className={"cursor-pointer"}
                      />
                    )}
                  </td>
                  <td>
                    {report.reported_at
                      ? dateTimeFormatter(report.reported_at)
                      : "-"}
                  </td>
                  <td className="text-right text-nowrap">
                    {report.id && (
                      <Button
                        size="sm"
                        variant="primary"
                        className="mr-2"
                        onClick={() => setSelectedReportId(report.id)}
                      >
                        {t("local_tax_daily_reports.actions.details")}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => sendDayReport(report.report_date)}
                      disabled={sendingMonth || hasPendingRows || monthClosed}
                    >
                      {report.status === "pending"
                        ? actionLabel("open")
                        : actionLabel(report.status)}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {selectedReportId && (
        <LocalTaxDailyReportDetailsDialog
          reportId={selectedReportId}
          handleClose={() => setSelectedReportId(null)}
          statusLabel={statusLabel}
        />
      )}

      {selectedOccupiedUnitsReport && (
        <EditOccupiedUnitsDialog
          report={selectedOccupiedUnitsReport}
          handleClose={() => setSelectedOccupiedUnitsReport(null)}
          handleSuccess={handleOccupiedUnitsSaved}
        />
      )}
    </div>
  );
};

export default DailyReportsPage;
